import {
  Link,
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  useNavigate,
  useRouterState,
  useRouteContext,
} from "@tanstack/react-router";
import React from "react";
import { ApiClientError, apiRequest } from "./lib/api";
import { authClient } from "./lib/auth-client";
import { isLocalAdminAuthMode } from "./lib/auth-mode";
import { captureAdminException, setAdminUserContext } from "./lib/sentry";
import type { AdminMeResult } from "./types";

function lazyRoute(loader: () => Promise<{ default: React.ComponentType }>) {
  const Component = React.lazy(loader);
  // Admin pages are operationally heavy; route-level splitting keeps the auth shell fast before data grids mount.
  return function LazyRouteComponent() {
    return React.createElement(Component);
  };
}

const DashboardPage = lazyRoute(() =>
  import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const ForbiddenPage = lazyRoute(() =>
  import("./pages/ForbiddenPage").then((m) => ({ default: m.ForbiddenPage })),
);
const OrganizationBillingPage = lazyRoute(() =>
  import("./pages/OrganizationBillingPage").then((m) => ({ default: m.OrganizationBillingPage })),
);
const LoginPage = React.lazy(() =>
  import("./pages/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const OrganizationsListPage = lazyRoute(() =>
  import("./pages/OrganizationsListPage").then((m) => ({ default: m.OrganizationsListPage })),
);
const UsersListPage = lazyRoute(() =>
  import("./pages/UsersListPage").then((m) => ({ default: m.UsersListPage })),
);

type ApiEnvelope<T> = {
  success: true;
  result: T;
};

type AdminAccess =
  | { kind: "login" }
  | { kind: "forbidden" }
  | { kind: "admin"; identity: AdminMeResult };

let cachedIdentity: AdminMeResult | null = null;
const localAdminAuthMode = isLocalAdminAuthMode();

async function resolveAdminAccess(): Promise<AdminAccess> {
  try {
    if (cachedIdentity) {
      return {
        kind: "admin",
        identity: cachedIdentity,
      };
    }

    const response = await apiRequest<ApiEnvelope<AdminMeResult>>("/api/admin/me");
    cachedIdentity = response.result;

    return {
      kind: "admin",
      identity: response.result,
    };
  } catch (error) {
    cachedIdentity = null;
    if (!(error instanceof ApiClientError && (error.status === 401 || error.status === 403))) {
      void captureAdminException(error, {
        action: "resolve-admin-access",
        feature: "auth-gate",
      });
    }
    if (error instanceof ApiClientError && (error.status === 401 || error.status === 403)) {
      if (localAdminAuthMode) {
        return { kind: "login" };
      }

      return { kind: "forbidden" };
    }

    return localAdminAuthMode ? { kind: "login" } : { kind: "forbidden" };
  }
}

function RootShell() {
  return (
    <React.Suspense fallback={null}>
      <Outlet />
    </React.Suspense>
  );
}

function AdminNotFound() {
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <section
        style={{
          width: 520,
          maxWidth: "100%",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          padding: 24,
          background: "#fff",
        }}
      >
        <h1 style={{ margin: "0 0 8px", fontSize: 24 }}>Route not found</h1>
        <p style={{ margin: "0 0 16px", color: "#64748b" }}>
          The requested admin route is not registered.
        </p>
        <Link to="/">Return to dashboard</Link>
      </section>
    </div>
  );
}

function ProtectedLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const context = useRouteContext({ from: "/_protected" });
  const identity = context.identity;

  React.useEffect(() => {
    // Refresh operator scope at layout level so every admin route inherits the current person, role, and auth mode without duplicating setup.
    setAdminUserContext({
      userId: identity.userId,
      email: identity.email,
      adminRole: identity.adminRole,
      authMode: localAdminAuthMode ? "better-auth-local" : "cloudflare-access",
      route: pathname,
    });
  }, [identity.adminRole, identity.email, identity.userId, pathname]);

  async function handleLogout() {
    cachedIdentity = null;
    try {
      await authClient.signOut();
      await navigate({ to: "/login" });
    } catch (error) {
      void captureAdminException(error, {
        action: "sign-out",
        feature: "auth",
      });
      throw error;
    }
  }

  const navItems = [
    { to: "/" as const, label: "Dashboard" },
    { to: "/users" as const, label: "Users" },
    { to: "/organizations" as const, label: "Organizations" },
  ];

  return (
    <div style={{ minHeight: "100vh", display: "grid", gridTemplateColumns: "248px 1fr" }}>
      <aside style={{ borderRight: "1px solid #e5e7eb", background: "#fff" }}>
        <div style={{ padding: 24 }}>
          <h1 style={{ margin: 0, fontSize: 24, lineHeight: 1.2 }}>Fluentry Admin</h1>
          <p style={{ margin: "6px 0 0", color: "#64748b", fontSize: 14 }}>
            {localAdminAuthMode
              ? "Local Better Auth development mode"
              : "Cloudflare Access governed operations console"}
          </p>
        </div>
        <nav aria-label="Admin navigation" style={{ display: "grid", gap: 4, padding: "0 12px" }}>
          {navItems.map((item) => {
            const selected = pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                style={{
                  display: "block",
                  borderRadius: 6,
                  padding: "10px 12px",
                  background: selected ? "#e0f2fe" : "transparent",
                  color: selected ? "#075985" : "#334155",
                  fontWeight: selected ? 600 : 500,
                  textDecoration: "none",
                }}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
      </aside>

      <div style={{ minWidth: 0, background: "#f8fafc" }}>
        <header
          style={{
            background: "#fff",
            borderBottom: "1px solid #e5e7eb",
            paddingInline: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>Signed in as</p>
            <strong>{identity.email || identity.userId}</strong>
          </div>

          {localAdminAuthMode ? (
            <button
              type="button"
              onClick={() => void handleLogout()}
              style={{
                border: "1px solid #d1d5db",
                borderRadius: 6,
                background: "#fff",
                padding: "8px 12px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Sign out
            </button>
          ) : (
            <div style={{ textAlign: "right" }}>
              <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>Session management</p>
              <strong>Cloudflare Access managed</strong>
            </div>
          )}
        </header>

        <main style={{ padding: 24 }}>
          <React.Suspense fallback={null}>
            <Outlet />
          </React.Suspense>
        </main>
      </div>
    </div>
  );
}

function LoginRoutePage() {
  const navigate = useNavigate();

  if (!localAdminAuthMode) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
        <section
          style={{
            width: 520,
            maxWidth: "100%",
            display: "grid",
            gap: 16,
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            padding: 24,
            background: "#fff",
          }}
        >
          <h1 style={{ margin: 0, fontSize: 24 }}>Fluentry Admin</h1>
          <p style={{ margin: 0 }}>管理画面のサインインは Cloudflare Access が管理しています。</p>
          <p style={{ margin: 0, color: "#64748b" }}>
            この画面が見えている場合は Access の認証導線ではなく、直接 `/login`
            へアクセスしています。通常は admin ルートへ移動すると Access が認証を要求します。
          </p>
          <Link to="/forbidden">権限エラーの説明を見る</Link>
        </section>
      </div>
    );
  }

  return (
    <LoginPage
      onAuthenticated={async () => {
        cachedIdentity = null;
        await navigate({ to: "/" });
      }}
    />
  );
}

const rootRoute = createRootRoute({
  component: RootShell,
  notFoundComponent: AdminNotFound,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginRoutePage,
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forbidden",
  component: ForbiddenPage,
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_protected",
  beforeLoad: async () => {
    const access = await resolveAdminAccess();

    if (access.kind === "login") {
      throw redirect({ to: "/login" });
    }

    if (access.kind === "forbidden") {
      throw redirect({ to: "/forbidden" });
    }

    return {
      identity: access.identity,
    };
  },
  component: ProtectedLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/",
  component: DashboardPage,
});

const usersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/users",
  component: UsersListPage,
});

const organizationsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/organizations",
  component: OrganizationsListPage,
});

const organizationBillingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/organizations/$organizationId/billing",
  component: OrganizationBillingPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  forbiddenRoute,
  protectedRoute.addChildren([
    dashboardRoute,
    usersRoute,
    organizationsRoute,
    organizationBillingRoute,
  ]),
]);

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function AdminApp() {
  return <RouterProvider router={router} />;
}
